<template>
  <ResponsiveModal
    :open="isMetalOpen"
    :aria-label="metalsLabel"
    :full-height="isMetalOpen && activeDetail !== null && !fromMineMarker"
    @close="closeLibrary"
  >
    <ContentSwap>
      <MetalDetail
        v-if="activeDetail"
        :key="`metal-${activeDetail}`"
        @close="closeMetals()"
      />
      <DetailLayout
        v-else
        key="metal-list"
        :title="metalsLabel"
        icon="/mine-my-phone/icons/pickaxe--flat.svg"
        @close="closeMetals()"
      >
        <MetalList />
      </DetailLayout>
    </ContentSwap>
  </ResponsiveModal>

  <ResponsiveModal
    :open="isContentOpen"
    :title="i18n(translations.contentButton)"
    icon="/mine-my-phone/icons/video--flat.svg"
    @close="closeToImpacts()"
  >
    <ContentList />
  </ResponsiveModal>

  <!-- Metal "quick view": a single-level detail layered on the current route
         via `?metal=`, with no list beneath. Last in the DOM so it paints on top. -->
  <ResponsiveModal
    :open="isQuickMetalOpen"
    :aria-label="metalsLabel"
    :full-height="isQuickMetalOpen && !fromMineMarker"
    layered
    @close="closeMetalQuickView()"
  >
    <ContentSwap>
      <MetalDetail
        :key="quickMetal!"
        :slug="quickMetal!"
        @close="closeMetalQuickView()"
      />
    </ContentSwap>
  </ResponsiveModal>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import ContentSwap from '~/scopes/campaign/components/UI/ContentSwap.vue'
import DetailLayout from '~/scopes/campaign/components/UI/DetailLayout.vue'
import ResponsiveModal from '~/scopes/campaign/components/UI/ResponsiveModal.vue'
import ContentList from '~/scopes/campaign/components/highlights/ContentList.vue'
import MetalDetail from '~/scopes/campaign/components/highlights/MetalDetail.vue'
import MetalList from '~/scopes/campaign/components/highlights/MetalList.vue'
import { useModalState } from '~/scopes/campaign/composables/useModalState'
import { MODAL_ROUTES } from '~/scopes/campaign/const/config.const'
import { metalBySlug } from '~/scopes/campaign/data/metals/data'

import translations from './ModalHighlights.translations'

const i18n = useI18n()
const {
  activeModal,
  activeDetail,
  quickMetal,
  quickMine,
  closeToImpacts,
  closeMetals,
  closeMetalQuickView,
  navigateToHome,
} = useModalState()

// The ✕ steps back a level (a metal detail to the list, the list out); a drag pushes
// the whole library away and lands on the map, without stopping at the list.
function closeLibrary(via?: 'drag') {
  if (via === 'drag') navigateToHome()
  else closeMetals()
}

const isMetalOpen = computed(
  () => activeModal.value === MODAL_ROUTES.METAL_LIBRARY,
)
const isContentOpen = computed(
  () => activeModal.value === MODAL_ROUTES.CONTENT_GALLERY,
)
// A `?metal=` quick view only opens for a slug that resolves to a real metal,
// so a bogus query can't surface an empty modal.
const isQuickMetalOpen = computed(() => metalBySlug(quickMetal.value) !== null)
// Same as the article sheet: full height unless a mine marker opened it, which the
// URL already says — only a marker carries `?mine=` (camera.md §2/§6).
const fromMineMarker = computed(() => quickMine.value !== null)
const metalsLabel = computed(() => i18n(translations.metalsButton))
</script>
